import * as THREE from "three";
import { Box3, Vector3 } from 'three';
import Engine from './Engine';
import Scenario from './scenarioElements/Scenario';
import Utils from './Utilities';
import {CustomMaterials} from './CustomMaterials';
import HighlightSphere from "./HighlightSphere";
import Time from './Time';
import Callout from './Callout';
import SceneObjectUtils from './utilities/SceneObjectUtils';
import ProjectSaver from './ProjectSaver';
import Step from "./scenarioElements/Step";
import ColorUtils from "./utilities/ColorUtils";
import ReduxStore, { StoreActions } from '../state/store';
import { CustomEventTypes, ChecklistFormType, ChecklistType, QuizQuestionStatus, EasingFunctions, QuizType, QuizRevealAnswers, ChecklistPriority, AllowRetriesOptions } from '../globals';
import { ChecklistItem, ChecklistOption, IChecklist, IChecklistData } from "../interfaces/checklistItem";

const tempBounds = new THREE.Box3();
const boundsSize = new THREE.Vector3();
const sphereMaterial = CustomMaterials.getGhostMaterial().clone();
const spherePulseDuration = 1.0;
const sphereDelayDuration = 0.5;

let sphereUpdateTimer = 0;

export default class QuizManager
{
	static minSphereDiameter = 0.01;
	static sphereDiameterScaler = 0.25;
	static quizObjectMap = new Map();
	static highlightSpheresMap = new Map();
	static backupCalloutsMap = new Map();
	
	static init() : void
	{
		document.addEventListener(CustomEventTypes.simulationStarted, QuizManager.onSimulationStarted);
		document.addEventListener(CustomEventTypes.simulationStopped, QuizManager.onSimulationStopped);
		sphereMaterial.depthTest = false;
	}

	static loadQuizObjects() : void
	{
		console.log('[QuizManager.loadQuizObjects] Starting to load quiz objects');
		Scenario.getSequences().forEach((sequence) =>
		{
			sequence.steps.forEach((step) =>
			{
				const question = QuizManager.getQuestion(step.id)
				if (step.scene && question && (question.type === ChecklistType.MULTI_OPTION_3D || question.type === ChecklistType.MULTI_OPTION_SELECT_3D))
				{
					console.log(`[QuizManager.loadQuizObjects] Found 3D question in step ${step.id}:`, question);
					for(const answer of question.options)
					{
						if(answer.object)
						{
							console.log(`[QuizManager.loadQuizObjects] Looking for object with ID: ${answer.object}`);
							const sceneObject = SceneObjectUtils.findObjectByGuid(Engine.getInstance().getSelectionRoot(step.scene.userData.guid), answer.object);
							if(sceneObject)
							{
								console.log(`[QuizManager.loadQuizObjects] Found and mapped object: ${answer.object}`);
								QuizManager.quizObjectMap.set(answer.object, sceneObject);
							}
							else
							{
								console.warn(`[QuizManager.loadQuizObjects] Could not find object with ID: ${answer.object}`);
							}
						}
					}
				}
			});
		});
		console.log(`[QuizManager.loadQuizObjects] Finished loading quiz objects. Total objects mapped: ${QuizManager.quizObjectMap.size}`);
	}

	static isQuizObject(objectId : string) : boolean
	{
		return QuizManager.quizObjectMap.has(objectId);
	}

	static getQuizSceneObject(objectId : string | undefined) : THREE.Object3D | undefined
	{
		if (!objectId)
		{
			console.log('[QuizManager.getQuizSceneObject] No object ID provided');
			return undefined;
		}
		const object = QuizManager.quizObjectMap.get(objectId);
		console.log(`[QuizManager.getQuizSceneObject] Looking for object with ID: ${objectId}, found: ${object ? 'yes' : 'no'}`);
		return object;
	}

	static onStepChange(step : Step) : void
	{
		if (!Utils.IsSimulationRunning()) return;
		
		const question = QuizManager.getQuestion(step.id);
		if(!question) return;

		QuizManager.generateHighlightSpheres(question);
		QuizManager.generateCallouts(question);
		ReduxStore.dispatch(StoreActions.checklist.setQuestionStatus(QuizManager.getAnswerStatus(step.id)));
		QuizManager.onAnswerStatusChanged(QuizManager.getAnswerStatus(step.id), step.id);
	}

	static onSimulationStarted(e) : void
	{
		const question = QuizManager.getQuestion();
		if(!question) return;

		QuizManager.generateHighlightSpheres(question);
		QuizManager.generateCallouts(question);
	}

	static onSimulationStopped(e) : void
	{
		QuizManager.clearHighlightSpheres();
		QuizManager.clearCallouts();
		ReduxStore.dispatch(StoreActions.checklist.setPreviewProperties({ next: "" }));
	}

	static update() : void
	{
		sphereUpdateTimer += Time.getDeltaTime();
		if(sphereUpdateTimer > spherePulseDuration + sphereDelayDuration) sphereUpdateTimer = 0.0;

		sphereMaterial.uniforms["u_alphaMultiplier"].value = 1 - EasingFunctions.easeInQuart(sphereUpdateTimer);

		for(const sphere of QuizManager.highlightSpheresMap.values())
		{
			sphere.setScale(sphereUpdateTimer > spherePulseDuration ? 0.0 : sphereUpdateTimer);
		}
	}

	static clearHighlightSpheres() : void
	{
		for(const sphere of QuizManager.highlightSpheresMap.values())
		{
			sphere.parent?.remove(sphere);
			SceneObjectUtils.disposeObject(sphere);
		}
		QuizManager.highlightSpheresMap.clear();
	}

	static generateHighlightSpheres(question) : void
	{
		QuizManager.clearHighlightSpheres();

		if(!question.showHighlights || question.type !== ChecklistType.MULTI_OPTION_3D && question.type !== ChecklistType.MULTI_OPTION_SELECT_3D) return;

		sphereUpdateTimer = 0.0;
		sphereMaterial.uniforms["u_ghostColor"].value.setHex("0x"+question.highlightColor.substring(1));

		for(const option of question.options)
		{
			const object = QuizManager.getQuizSceneObject(option.object);
			if(!object) continue;
			tempBounds.makeEmpty();
			SceneObjectUtils.expandBoundsByObjectVisibleOnly(tempBounds, object);
			tempBounds.getSize(boundsSize);
			const sphereGeometry = new THREE.SphereGeometry(0.5, 32, 32);
			const highlightSphere = new HighlightSphere(sphereGeometry, sphereMaterial);
			highlightSphere.geometry.scale(question.highlightSphereDiameter, question.highlightSphereDiameter, question.highlightSphereDiameter);
			if(tempBounds.isEmpty())
			{
				object.getWorldPosition(highlightSphere.position);
			}
			else
			{
				tempBounds.getCenter(highlightSphere.position);
			}
			
			object.attach(highlightSphere);
			QuizManager.highlightSpheresMap.set(option.object, highlightSphere);
		}
	}

	static generateCallouts(question : ChecklistItem) : void
	{
		QuizManager.clearCallouts();

		if(question.type !== ChecklistType.MULTI_OPTION_3D && question.type !== ChecklistType.MULTI_OPTION_SELECT_3D) return;
		const answers = ReduxStore.getState().checklistModule.answers[Scenario.currentStep()?.id || ""];
		for(let i = 0; i < question.options.length; i++)
		{
			const option : ChecklistOption = question.options[i];
			const object = QuizManager.getQuizSceneObject(option.object);
			if(!object || !object.userData.annotation?.enabled) continue;

			const backupCallout = JSON.parse(JSON.stringify(object.userData.annotation));
			QuizManager.backupCalloutsMap.set(option.object, backupCallout);

			if(option.object)
			{
				const callout = Callout.getCallout(option.object);
			
				if(callout)
				{
					if(answers && answers[i])
					{
						const formattedColor = ColorUtils.hexToRGBA(question.highlightColor!);
						callout.setTitleBackground(formattedColor);
						callout.setLineColor(formattedColor);
					}
					else
					{
						callout.setTitleBackground(backupCallout.titleBackground);
					
						callout.setLineColor(backupCallout.titleBackground);
					}
	
					callout.setTitle(String.fromCharCode(65 + i));
					callout.setDescriptionEnabled(false);
					callout.setStrokeEnabled(false);
				}
			}
		}
	}

	static clearCallouts() : void
	{
		for(const [guid, backupCallout] of QuizManager.backupCalloutsMap.entries())
		{
			const callout = Callout.getCallout(guid);
			if(callout)
			{
				callout.setTitle(backupCallout.title);
				callout.setTitleColor(backupCallout.titleColor);
				callout.setTitleBackground(backupCallout.titleBackground);
				callout.setLineColor(backupCallout.lineColor);
				callout.setDescriptionEnabled(backupCallout.descriptionEnabled);
				callout.setStrokeEnabled(backupCallout.strokeEnabled);
			}
		}
		QuizManager.backupCalloutsMap.clear();
	}
	
	static calculateQuizScore() : number
	{
		//first we need to isolate all the quiz steps
		const checklist : IChecklistData = ReduxStore.getState().checklistModule.checklist;
		const questions : {[stepId: string]: ChecklistItem} = {};
		for (const [step, data] of Object.entries(checklist))
		{
			if (data.type === ChecklistFormType.QUIZ)
			{
				questions[step] = data.questions[0];
			}
		}

		const answers = ReduxStore.getState().checklistModule.answers;
		let maxScore = 0;
		let currentScore = 0;
		for (const [step, question] of Object.entries(questions))
		{
			maxScore += question.score ?? 0;
			if (!answers[step]) continue; //if this question wasn't answered skip

			let answeredCorrectly = true;
			for (let i = 0; i < question.options.length; i++)
			{ //compare answers to options to determine if the question was answered correctly
				const option = question.options[i];
				const answer = answers[step][i];
				if ((option.correct === true && answer !== true) || (option.correct !== true && answer === true))
				{
					answeredCorrectly = false;
					break;
				}
			}

			if (answeredCorrectly)
			{ 
				currentScore += question.score!; 
			}
		}

		//calculate current score vs max score
		const result = Math.round((currentScore / maxScore) * 100);
		return Number.isNaN(result) ? 0 : result;
	}

	static areThereAnyGradedQuestions() : boolean
	{
		const checklist : IChecklistData = ReduxStore.getState().checklistModule.checklist;
		for(const stepChecklist of Object.values(checklist))
		{
			for(const question of stepChecklist.questions)
			{
				if(question.score! > 0)
				{
					return true;
				}
			}
		}

		return false;
	}

	static getQuestion(stepId = Scenario.currentStep()?.id || "") : ChecklistItem | null
	{
		const checklist : IChecklist = ReduxStore.getState().checklistModule.checklist[stepId];
		if(checklist && checklist.type === ChecklistFormType.QUIZ && checklist.questions[0]) return checklist.questions[0];
		return null;
	}

	static findOption(objectId, stepId = Scenario.currentStep()?.id) : number
	{
		console.log(`[QuizManager.findOption] Looking for option with object ID: ${objectId} in step: ${stepId}`);
		const question = QuizManager.getQuestion(stepId);
		if(!question)
		{
			console.log('[QuizManager.findOption] No question found for this step');
			return -1;
		}
		
		if(question.type !== ChecklistType.MULTI_OPTION_3D && question.type !== ChecklistType.MULTI_OPTION_SELECT_3D)
		{
			console.log(`[QuizManager.findOption] Question type is not 3D: ${question.type}`);
			return -1;
		}
		
		for(let i = 0; i < question.options.length; i++)
		{
			const answer = question.options[i];
			if(answer.object === objectId)
			{
				console.log(`[QuizManager.findOption] Found option at index: ${i}`);
				return i;
			}
		}

		console.log('[QuizManager.findOption] No matching option found');
		return -1;
	}

	static toggle3DAnswer(objectId : string) : void
	{
		console.log(`[QuizManager.toggle3DAnswer] Toggling answer for object ID: ${objectId}`);
		if(ReduxStore.getState().checklistModule.questionStatus !== QuizQuestionStatus.UNANSWERED)
		{
			console.log('[QuizManager.toggle3DAnswer] Question already answered, ignoring toggle');
			return;
		}

		const question : ChecklistItem | null = QuizManager.getQuestion();
		if(question && question.type !== ChecklistType.MULTI_OPTION_3D && question.type !== ChecklistType.MULTI_OPTION_SELECT_3D)
		{
			console.log(`[QuizManager.toggle3DAnswer] Question type is not 3D: ${question.type}`);
			return;
		}

		const answerIndex = QuizManager.findOption(objectId); 
		if(answerIndex >= 0)
		{
			console.log(`[QuizManager.toggle3DAnswer] Setting answer at index: ${answerIndex}`);
			QuizManager.setAnswer(answerIndex);
			if(QuizManager.highlightSpheresMap.has(objectId))
			{
				const highlightSphere = QuizManager.highlightSpheresMap.get(objectId);
				highlightSphere.visible = !QuizManager.getAnswer(objectId);
				console.log(`[QuizManager.toggle3DAnswer] Toggled highlight sphere visibility to: ${highlightSphere.visible}`);
			}
			document.dispatchEvent(new CustomEvent(CustomEventTypes.toggleQuizAnswer, { detail: { index: answerIndex } }));
		}
		else
		{
			console.log(`[QuizManager.toggle3DAnswer] No matching option found for object ID: ${objectId}`);
		}
	}

	static getAnswer(objectId: string) : boolean
	{
		const stepId = Scenario.currentStep()?.id;
		const answerIndex = QuizManager.findOption(objectId);
		const answers = ReduxStore.getState().checklistModule.answers;
		if(answers && stepId && answers[stepId] && answers[stepId][answerIndex] === true) return true;
		return false;
	}

	static setAnswer(answerIndex: number, value?: boolean) : void
	{
		console.log(`[QuizManager.setAnswer] Setting answer at index: ${answerIndex}, value: ${value !== undefined ? value : 'toggling'}`);
		const question = QuizManager.getQuestion();
		if(!question)
		{
			console.log('[QuizManager.setAnswer] No question found');
			return;
		}

		const questionOptions = question.options;
		let answers = [ ...(ReduxStore.getState().checklistModule.answers[Scenario.currentStep()?.id || ""] || []) ];
		if (!answers) 
		{
			const length = questionOptions ? questionOptions.length : 1;
			answers = Array.from({ length }, () => null);
			console.log(`[QuizManager.setAnswer] Created new answers array with length: ${length}`);
		}

		if(!value)
		{
			value = answers[answerIndex] === true ? false : true;
			console.log(`[QuizManager.setAnswer] Toggled value to: ${value}`);
		}

		if(question.type === ChecklistType.MULTI_OPTION || question.type === ChecklistType.MULTI_OPTION_3D)
		{
			console.log('[QuizManager.setAnswer] Resetting all answers for MULTI_OPTION type');
			for(let i = 0 ; i < answers.length; i++ )
			{
				answers[i] = null;
				if(QuizManager.highlightSpheresMap.has(question.options[i].object))
				{
					const highlightSphere = QuizManager.highlightSpheresMap.get(question.options[i].object);
					highlightSphere.visible = true;
				}
			}
		}

		const answerObjectId = question.options[answerIndex].object;
		console.log(`[QuizManager.setAnswer] Answer object ID: ${answerObjectId}`);

		if(question.type === ChecklistType.MULTI_OPTION_SELECT_3D && answerObjectId && QuizManager.backupCalloutsMap.has(answerObjectId))
		{
			console.log('[QuizManager.setAnswer] Updating callout for MULTI_OPTION_SELECT_3D');
			const callout = Callout.getCallout(answerObjectId);
			let color;
			if(value)
			{
				color = ColorUtils.hexToRGBA(question.highlightColor!);
			}
			else
			{
				const backupCallout = QuizManager.backupCalloutsMap.get(answerObjectId);
				color = backupCallout.titleBackground;
			}
			
			if(callout)
			{
				callout.setTitleBackground(color);
				callout.setLineColor(color);
			}
		}
		else if(question.type === ChecklistType.MULTI_OPTION_3D)
		{
			console.log('[QuizManager.setAnswer] Updating callouts for MULTI_OPTION_3D');
			for(let i = 0 ; i < answers.length; i++ )
			{
				const indexObjectId = question.options[i].object;
				if(indexObjectId && QuizManager.backupCalloutsMap.has(indexObjectId))
				{
					const callout = Callout.getCallout(indexObjectId);
					let color;
					if(i === answerIndex && value) 
					{
						color = ColorUtils.hexToRGBA(question.highlightColor!);
					}
					else
					{
						const backupCallout = QuizManager.backupCalloutsMap.get(indexObjectId);
						color = backupCallout.titleBackground;
					}
	
					if(callout)
					{
						callout.setTitleBackground(color);
						callout.setLineColor(color);
					}
				}
			}
		}

		answers[answerIndex] = value;
		console.log(`[QuizManager.setAnswer] Final answers array:`, answers);
		ReduxStore.dispatch(StoreActions.checklist.setAnswers({ step: Scenario.currentStep()?.id || "", answers: answers }));
	}

	static getAnswerStatus(stepId = Scenario.currentStep()?.id || "")
	{
		const state = ReduxStore.getState();
		const question = QuizManager.getQuestion(stepId);
		const answers = state.checklistModule.answers[stepId];
		const attempts = state.checklistModule.attempts[stepId];

		if(!answers || !question) return QuizQuestionStatus.UNANSWERED;

		if(question.options && question.options.length > 0)
		{
			for(let i = 0; i < question.options.length; i++)
			{
				const answer = answers[i];
				if((answer !== true && question.options[i].correct === true) ||
                        (answer === true && question.options[i].correct !== true)
				)
				{
					if(attempts !== 0) return QuizQuestionStatus.UNANSWERED;
					return QuizQuestionStatus.INCORRECT;
				}
			}
		}

		return QuizQuestionStatus.CORRECT;
	}

	static getQuizType() : QuizType | null
	{
		let result : QuizType | null = null;
		const checklist : IChecklistData = ReduxStore.getState().checklistModule.checklist;
		for(const [stepId, stepChecklist] of Object.entries(checklist))
		{
			if(!Scenario.getStep(stepId) || Scenario.getStep(stepId)?.isDeleted()) continue;
			if(stepChecklist.type === ChecklistFormType.QUIZ && stepChecklist.questions.length > 0)
			{
				result = QuizType.UNGRADED;
				if(stepChecklist.questions[0].score! > 0)
				{
					return ReduxStore.getState().checklistModule.quizSettings.type;
				}
				
			}
		}

		return result;
	}

	static resetAnswers() : void 
	{
		const question = QuizManager.getQuestion();
		if(!question) return;
	
		const questionOptions = question.options;
		const clearAnswers = Array.from({ length: questionOptions ? questionOptions.length : 1 }, () => null);
		ReduxStore.dispatch(StoreActions.checklist.setAnswers({ step: Scenario.currentStep()?.id || "", answers: clearAnswers }));
		ReduxStore.dispatch(StoreActions.checklist.setQuestionStatus(QuizQuestionStatus.UNANSWERED));
		QuizManager.generateHighlightSpheres(question);
		QuizManager.generateCallouts(question);
	}

	static GetCurrentNumberOfRetries() : number
	{
		return ReduxStore.getState().checklistModule.currentNumberOfRetries;
	}

	static SetCurrentNumberOfRetries(value : number) : void
	{
		ReduxStore.dispatch(StoreActions.checklist.setCurrentNumberOfRetries(value));
	}

	static shouldAllowRetry() : boolean
	{
		const quizSettings =  ReduxStore.getState().checklistModule.quizSettings;
		if(quizSettings.type === QuizType.UNGRADED) return false;
		if(quizSettings.type === QuizType.GRADED && quizSettings.allowRetries !== AllowRetriesOptions.NEVER) 
		{
			if(ReduxStore.getState().checklistModule.currentNumberOfRetries > 0)
			{
				switch(quizSettings.allowRetries)
				{
					case AllowRetriesOptions.FAIL_ONLY:
						if(QuizManager.calculateQuizScore() < quizSettings.passingScore)
						{
							return true;
						}
						break;
					case AllowRetriesOptions.PASS_OR_FAIL:
						return true;
				}
			}
		}
		return false;
	}

	static onAnswerStatusChanged(status: QuizQuestionStatus, stepId: string = Scenario.currentStep()?.id || "") : void
	{
		if(!stepId || status === QuizQuestionStatus.UNANSWERED) return;

		const remainingAttempts = ReduxStore.getState().checklistModule.attempts[stepId];
		QuizManager.clearHighlightSpheres();
		const answers = ReduxStore.getState().checklistModule.answers[stepId] ? [...ReduxStore.getState().checklistModule.answers[stepId]] : [];
		const question = QuizManager.getQuestion();

		if(!question) return;

		for(let i = 0 ; i < answers.length; i++ )
		{
			const indexObjectId = question.options[i].object;
			if(indexObjectId && QuizManager.backupCalloutsMap.has(indexObjectId))
			{
				const callout = Callout.getCallout(indexObjectId);
				let color;
				let showFullName = status === QuizQuestionStatus.CORRECT || (remainingAttempts === 0 && this.shouldRevealAnswers());
				const isCorrectAnswer = question.options[i].correct;
				const isSelectedAnswer = answers[i];

				if(isSelectedAnswer)
				{
					if(question.type === ChecklistType.MULTI_OPTION_3D || remainingAttempts === 0)
					{
						showFullName = true;
						if(isCorrectAnswer)
						{
							color = { r: 98, g: 166, b: 81, a: 100 };
						}
						else
						{
							color = { r: 145, g: 51, b: 51, a: 100 };
						}
					}
					else if(isCorrectAnswer && status === QuizQuestionStatus.CORRECT)
					{
						color = { r: 98, g: 166, b: 81, a: 100 };
					}
					if(callout)
						callout.setTitleColor({ r: 255, g: 255, b: 255 });
				}
				else
				{
					if(callout && (remainingAttempts > 0 || !isCorrectAnswer))
					{
						color = {...callout.getTitleBackground(), a: 40 };
					}
					else
					{
						showFullName = true;
					}
				}

				if(callout && color)
				{
					callout.setLineColor(color);
					callout.setTitleBackground(color);
				}
				if(callout && showFullName)
				{
					callout.setTitle(String.fromCharCode(65 + i) + ") " + question.options[i].description);
				}
			}
		}
	}

	static shouldRevealAnswers() : boolean
	{
		const quizSettings =  ReduxStore.getState().checklistModule.quizSettings;
		if(quizSettings.revealAnswers === QuizRevealAnswers.ALWAYS) return true;
		if(quizSettings.revealAnswers === QuizRevealAnswers.GRADED && quizSettings.type === QuizType.GRADED) return true;
		if(quizSettings.revealAnswers === QuizRevealAnswers.UNGRADED && quizSettings.type === QuizType.UNGRADED) return true;
		return false;
	}

	static preProjectPackage() : void
	{
		for(const object of QuizManager.quizObjectMap.values())
		{
			object.traverse(function (child) 
			{
				if (child.userData.triggers?.length)
				{
					child.userData.discardTriggerExport = true;
				}
			});
		}
	}

	static postProjectPackage() : void
	{
		for(const object of QuizManager.quizObjectMap.values())
		{
			object.traverse(function (child) 
			{
				delete child.userData.discardTriggerExport;
			});
		}
	}

	static packQuizReduxData(objectGuidToNodeIndexMap: Map<string, number> | undefined = undefined) : IChecklistData
	{
		const result = {}
		const sequenceData = Scenario.export(objectGuidToNodeIndexMap);
		const checklistData = ProjectSaver.saveChecklistData();
		
		for (const [key, value] of Object.entries(checklistData))
		{
			if (sequenceData.steps[key] && value.type !== ChecklistFormType.NONE)
			{
				const valueObject = JSON.parse(JSON.stringify(value)); // deep copy to avoid frozen objects
				if (value.type === ChecklistFormType.QUIZ)
				{
					valueObject.items = [...value.questions];
				}

				for (let i = 0; i < valueObject.items.length; i++)
				{ //clear id from questions and answers
					const itemCopy = JSON.parse(JSON.stringify(valueObject.items[i]));
					if(itemCopy.type !== ChecklistType.TEXT && itemCopy.type !== ChecklistType.NUMBER && itemCopy.type !== ChecklistType.CHECKBOX )
					{
						delete itemCopy.prefill;
					}

					if(value.behaviour === ChecklistPriority.PASSIVE)
					{
						itemCopy.mustRespond = false;
					}
					
					if (itemCopy.options?.length)
					{
						const is3DQuestion = itemCopy.type === ChecklistType.MULTI_OPTION_3D || itemCopy.type === ChecklistType.MULTI_OPTION_SELECT_3D;
						for (let j = 0; j < itemCopy.options.length; j ++)
						{
							const optionCopy = JSON.parse(JSON.stringify(itemCopy.options[j]));
							delete optionCopy.id;
							delete optionCopy.next;
							if(objectGuidToNodeIndexMap && optionCopy.object)
							{
								const objectGuid = optionCopy.object;
								optionCopy.object = objectGuidToNodeIndexMap.get(objectGuid);
								if(is3DQuestion)
								{
									const object = QuizManager.getQuizSceneObject(objectGuid);
									const bounds = new Box3();
									SceneObjectUtils.expandBoundsByObjectVisibleOnly(bounds, object);

									if(bounds.isEmpty())
									{
										optionCopy.highlightSphereOffset = {x: 0, y: 0, z: 0};
									}
									else
									{
										const boundsCenter = new Vector3();
										const worldPosition = new Vector3();
										object?.getWorldPosition(worldPosition);
										bounds.getCenter(boundsCenter);
										optionCopy.highlightSphereOffset = {x: boundsCenter.x - worldPosition.x, y: boundsCenter.y - worldPosition.y, z: boundsCenter.z - worldPosition.z}; 
									}
								}
							}
							itemCopy.options[j] = optionCopy;
						}

						if(is3DQuestion)
						{
							itemCopy.highlightSphereSize = itemCopy.highlightSphereDiameter;
						} 
					}
					delete itemCopy.highlightSphereDiameter;
					delete itemCopy.id;
					valueObject.items[i] = itemCopy;
				}

				delete valueObject.questions;
				result[key] = valueObject;
			}
		}
		return result;
	}
}